/* ══════════════════════════════════════════════════════════════════════
   NETWORK GRAPH — legend, which is also the filter.

   Two tiers, because they filter on different things:

     Entity type  hides a kind AND everything descending from it, so
                  switching off "Accounts" takes the identities and their
                  policies with it rather than leaving orphans floating.
     Risk         applies to policy nodes only, matching on the risk that
                  colours the policy's label.

   A disabled entry drops to 0.3 opacity, so the legend still reads as a
   key while showing which way each switch is thrown.
   ══════════════════════════════════════════════════════════════════════ */
import {
  PAG_KIND_META,
  PAG_KIND_ORDER,
  pagNetKindColor,
  pagNetRiskColor,
  PAG_SEVERITY_META,
  PAG_SEVERITY_ORDER,
  type PagNodeKind,
  type PagSeverity,
} from "../../data/pagTypes";
import type { NetSurface } from "./networkGraphVisuals";

const DISABLED_OPACITY = 0.3;

/** Centred on the right edge, which is also clear of Policy Insights'
 *  top-right search box -- so both graphs use this and neither needs an
 *  override to dodge its own chrome. */
const DEFAULT_POSITION =
  "absolute right-3 top-1/2 z-10 -translate-y-1/2 sm:right-4";

interface NetworkLegendProps {
  surface: NetSurface;
  hiddenKinds: Set<PagNodeKind>;
  hiddenRisks: Set<PagSeverity>;
  onToggleKind: (kind: PagNodeKind) => void;
  onToggleRisk: (risk: PagSeverity) => void;
  /** Overrides the panel's placement classes only; the panel's own look is
   *  fixed so both graphs cannot drift apart. */
  positionClass?: string;
}

export function NetworkLegend({
  surface,
  hiddenKinds,
  hiddenRisks,
  onToggleKind,
  onToggleRisk,
  positionClass = DEFAULT_POSITION,
}: NetworkLegendProps) {
  return (
    <div
      className={`${positionClass} w-[124px] rounded-lg px-2.5 py-2 sm:w-[152px] sm:px-3.5 sm:py-3`}
      style={{ background: surface.panel, border: `1px solid ${surface.panelBorder}` }}
    >
      <div className="flex flex-col gap-1.5">
        {PAG_KIND_ORDER.map((kind) => {
          const on = !hiddenKinds.has(kind);
          return (
            <button
              key={kind}
              type="button"
              aria-pressed={on}
              onClick={() => onToggleKind(kind)}
              className="flex items-center gap-2 rounded text-left transition-opacity focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400"
              style={{ opacity: on ? 1 : DISABLED_OPACITY }}
            >
              <span
                className="size-2.5 flex-shrink-0 rounded-full"
                style={{ background: pagNetKindColor(kind, surface.isDark) }}
              />
              <span
                className="text-[10px] font-medium sm:text-[11px]"
                style={{ color: surface.label }}
              >
                {PAG_KIND_META[kind].label}
              </span>
            </button>
          );
        })}
      </div>

      <div
        className="mt-2.5 flex flex-col gap-1 pt-2.5"
        style={{ borderTop: `1px solid ${surface.panelBorder}` }}
      >
        {PAG_SEVERITY_ORDER.map((risk) => {
          const on = !hiddenRisks.has(risk);
          return (
            <button
              key={risk}
              type="button"
              aria-pressed={on}
              onClick={() => onToggleRisk(risk)}
              className="rounded text-left text-[10px] font-medium transition-opacity focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400 sm:text-[11px]"
              style={{
                color: pagNetRiskColor(risk, surface.isDark),
                opacity: on ? 1 : DISABLED_OPACITY,
              }}
            >
              {PAG_SEVERITY_META[risk].label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
